/**
 * Marketplace Type Definitions
 *
 * TypeScript types for marketplace entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 */

import type { EnvironmentVariable } from './application.types';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Marketplace listing status
 */
export const MarketplaceListingStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  UNPUBLISHED: 'unpublished',
} as const;

export type MarketplaceListingStatus = typeof MarketplaceListingStatus[keyof typeof MarketplaceListingStatus];

/**
 * Application source type - tenant-created or from marketplace
 */
export const ApplicationSourceType = {
  TENANT: 'tenant',
  MARKETPLACE: 'marketplace',
} as const;

export type ApplicationSourceType = typeof ApplicationSourceType[keyof typeof ApplicationSourceType];

/**
 * Marketplace categories
 * NOTE: Must match backend enum in tenant-api/src/validators/marketplace.ts
 */
export const MarketplaceCategory = {
  OPERATIONS: 'operations',
  ANALYTICS: 'analytics',
  FINANCE: 'finance',
  HR: 'hr',
  SALES: 'sales',
  MARKETING: 'marketing',
  PRODUCTIVITY: 'productivity',
  INTEGRATIONS: 'integrations',
  UTILITIES: 'utilities',
  OTHER: 'other',
} as const;

export type MarketplaceCategory = typeof MarketplaceCategory[keyof typeof MarketplaceCategory];

// ============================================================================
// VERSION
// ============================================================================

/**
 * Marketplace version information
 */
export interface MarketplaceVersion {
  version: string;
  releaseNotes?: string;
  codeBundle?: string;
  codeBundleKey?: string;
  publishedAt: string;
  // Application metadata (copied from source application at publish time)
  actions?: string[];
  menu?: string;
  submenu?: string;
  environmentVariables?: EnvironmentVariable[];
}

// ============================================================================
// MARKETPLACE LISTING ENTITY
// ============================================================================

/**
 * Marketplace metadata attached to installed applications
 */
export interface MarketplaceMetadata {
  listingId: string;
  publisherTenantId: string;
  publisherTenantCode: string;
  installedVersion: string;
  installedAt: string;
  installedBy: string;
}

/**
 * Marketplace listing - published application available for installation
 */
export interface MarketplaceListing {
  listingId: string;
  publisherTenantId: string;
  publisherTenantCode: string;
  sourceApplicationId: string;
  name: string;
  description: string;
  category: MarketplaceCategory;
  iconUrl?: string;
  screenshotUrls?: string[];
  documentationUrl?: string;
  repositoryUrl?: string;
  availableVersions: MarketplaceVersion[];
  latestVersion?: string;
  status: MarketplaceListingStatus;
  installCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for creating a marketplace listing (publishing)
 * NOTE: Field names must match backend MarketplaceListingCreateDTO
 */
export interface MarketplaceListingCreateDTO {
  sourceApplicationId: string;
  deploymentId: string; // Deployment with versionTag to publish
  name: string;
  description: string;
  category: MarketplaceCategory;
  documentationUrl?: string;
  repositoryUrl?: string;
  releaseNotes?: string;
}

/**
 * DTO for updating a marketplace listing
 */
export interface MarketplaceListingUpdateDTO {
  name?: string;
  description?: string;
  category?: MarketplaceCategory;
  iconUrl?: string;
  screenshotUrls?: string[];
  documentationUrl?: string;
  repositoryUrl?: string;
}

/**
 * DTO for adding a new version
 */
export interface MarketplaceVersionCreateDTO {
  deploymentId: string; // Deployment with versionTag to publish as new version
  releaseNotes?: string;
}

/**
 * DTO for installing a marketplace app
 * NOTE: confirmationCode must match applicationCode (backend validation)
 */
export interface MarketplaceInstallDTO {
  listingId: string;
  version: string; // Required - SemVer format
  applicationCode: string;
  confirmationCode: string; // Must match applicationCode
}

/**
 * DTO for upgrading an installed marketplace app
 */
export interface MarketplaceUpgradeDTO {
  targetVersion?: string;
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Marketplace search query parameters
 */
export interface MarketplaceSearchQuery {
  search?: string;
  category?: MarketplaceCategory;
  limit?: number;
  cursor?: string;
}

// ============================================================================
// RESPONSE TYPES
// ============================================================================

/**
 * Paginated marketplace listings response
 */
export interface MarketplaceListingsResponse {
  items: MarketplaceListing[];
  cursor?: string;
  hasMore: boolean;
}

/**
 * Available update information
 */
export interface AvailableUpdate {
  applicationId: string;
  applicationName: string;
  listingId: string;
  currentVersion: string;
  latestVersion: string;
  releaseNotes?: string;
}

/**
 * Check for updates response
 */
export interface CheckUpdatesResponse {
  updates: AvailableUpdate[];
}

/**
 * Category metadata for display
 */
export interface CategoryInfo {
  value: MarketplaceCategory;
  label: string;
  description: string;
}

/**
 * All marketplace categories with metadata
 * NOTE: Must match backend enum in tenant-api/src/validators/marketplace.ts
 */
export const MARKETPLACE_CATEGORIES: CategoryInfo[] = [
  { value: 'operations', label: 'Operations', description: 'Business operations and workflows' },
  { value: 'analytics', label: 'Analytics', description: 'Data analysis and reporting tools' },
  { value: 'finance', label: 'Finance', description: 'Financial management and accounting' },
  { value: 'hr', label: 'HR', description: 'Human resources and employee management' },
  { value: 'sales', label: 'Sales', description: 'Sales tracking and management' },
  { value: 'marketing', label: 'Marketing', description: 'Marketing automation and campaigns' },
  { value: 'productivity', label: 'Productivity', description: 'Productivity and collaboration tools' },
  { value: 'integrations', label: 'Integrations', description: 'Third-party service integrations' },
  { value: 'utilities', label: 'Utilities', description: 'General utility applications' },
  { value: 'other', label: 'Other', description: 'Other applications' },
];
